import type { NotificationTemplateDTO, OfferNotificationTemplateChannel } from '@today/types'
import { apiClient } from './api.client'

/** The create body of a template (TO-140 offer-notification-templates Contract). */
export interface NotificationTemplateInput {
	name: string
	description: string | null
	channelType: OfferNotificationTemplateChannel
	locale: string
	subject: string | null
	body: string
	isDefaultForChannel: boolean
	isActive: boolean
}

/** The list's optional filters (`filters[locale]`, `filters[channelType]`, `filters[isActive]`). */
export interface NotificationTemplateFilters {
	locale?: string
	channelType?: OfferNotificationTemplateChannel
	isActive?: boolean
}

/** `POST /offers/templates/preview` body: the draft text and the offer to render it against. */
export interface NotificationTemplatePreviewBody {
	offerId: string
	subject: string | null
	body: string
}

/** The rendered draft. */
export interface NotificationTemplatePreview {
	subject: string | null
	body: string
}

const BASE = '/offers/templates'
const template = (id: string) => `${BASE}/${encodeURIComponent(id)}`

/** The set filters as `filters[key]` query parameters. */
const filterParams = (filters: NotificationTemplateFilters): Record<string, string> => {
	const params: Record<string, string> = {}
	if (filters.locale !== undefined) params['filters[locale]'] = filters.locale
	if (filters.channelType !== undefined) params['filters[channelType]'] = filters.channelType
	if (filters.isActive !== undefined) params['filters[isActive]'] = String(filters.isActive)
	return params
}

/** The tenant's offer notification templates (TO-140). */
export const notificationTemplateService = {
	/** `GET /offers/templates` — in library order (channel type, locale, name, id). */
	list: async (filters: NotificationTemplateFilters = {}) =>
		(await apiClient.get<{ items: NotificationTemplateDTO[] }>(BASE, { params: filterParams(filters) })).data.items,
	/** `GET /offers/templates/{id}`. */
	get: async (id: string) => (await apiClient.get<NotificationTemplateDTO>(template(id))).data,
	/** `POST /offers/templates` — `notification_templates.manage`. */
	create: async (input: NotificationTemplateInput) => (await apiClient.post<NotificationTemplateDTO>(BASE, input)).data,
	/** `PATCH /offers/templates/{id}` — any non-empty subset of the create fields. */
	update: async (id: string, patch: Partial<NotificationTemplateInput>) =>
		(await apiClient.patch<NotificationTemplateDTO>(template(id), patch)).data,
	/** `DELETE /offers/templates/{id}` — a soft delete (`204`). */
	remove: async (id: string) => {
		await apiClient.delete(template(id))
	},
	/** `POST /offers/templates/preview` — renders unsaved text against one of the tenant's offers. */
	preview: async (body: NotificationTemplatePreviewBody) =>
		(await apiClient.post<NotificationTemplatePreview>(`${BASE}/preview`, body)).data,
	/** `PUT /offers/templates/{id}/default` — idempotent; the template is then its pair's one default (TO-629). */
	setDefault: async (id: string) => (await apiClient.put<NotificationTemplateDTO>(`${template(id)}/default`)).data,
}
